import { CompanySearchSelect } from "@/components/companies/CompanySearchSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ContactFilterState, DMFilter } from "@/hooks/useContactFilters";
import { cn } from "@/lib/utils";

const ANY_VALUE = "__any__";

const ENRICHMENT_OPTIONS = [
  { value: "enriched", label: "Enriched" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "none", label: "None" },
];

/** The Contacts page's filters — company, decision maker, enrichment —
 *  on a contact filter state, wherever contacts are listed with them. */
export function ContactFilterControls({
  slug,
  state,
  compact = false,
}: {
  slug: string;
  state: ContactFilterState;
  /** The wizard's denser toolbar. */
  compact?: boolean;
}) {
  const trigger = cn("rounded-lg border-slate-200 bg-white", compact ? "h-8 text-xs" : "h-9");
  return (
    <>
      <CompanySearchSelect
        key={slug}
        slug={slug}
        value={state.companyFilter}
        selected={state.companyFilter === null ? null : (state.companyById.get(state.companyFilter) ?? null)}
        onChange={state.pickCompany}
        compact={compact}
      />

      <Select value={state.dmFilter} onValueChange={(v) => state.setDmFilter(v as DMFilter)}>
        <SelectTrigger className={cn(trigger, compact ? "w-[120px]" : "w-[160px]")} aria-label="Decision maker">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Any role</SelectItem>
          <SelectItem value="yes">Decision makers</SelectItem>
          <SelectItem value="no">Non-DM</SelectItem>
        </SelectContent>
      </Select>

      <Select
        value={state.enrichmentFilter ?? ANY_VALUE}
        onValueChange={(v) => state.setEnrichmentFilter(v === ANY_VALUE ? null : v)}
      >
        <SelectTrigger className={cn(trigger, compact ? "w-[140px]" : "w-[170px]")} aria-label="Enrichment">
          <SelectValue placeholder="Any enrichment" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY_VALUE}>Any enrichment</SelectItem>
          {ENRICHMENT_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  );
}
